import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Cards from 'bundles/apex/card/container';
import SelectedOptions from 'components/multiselect_modal/options/selected';
import SettingsContext from 'components/multiselect_modal/settings_context';
import AvailableOptions from 'components/multiselect_modal/options/available';
import Button from 'shared/button';
import { put } from 'helpers/api';
import { Banner } from '@buildoutinc/blueprint-react/ui/Banner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { Modal } from 'react-bootstrap-5';

export default function DownloadableReportFieldSelector({
  documentPath,
  pdfFields,
  selectedFields,
  inventoryReport,
  onSubmit,
}) {
  const [showModal, setShowModal] = useState(false);
  const [selection, setSelection] = useState(pdfFields.filter(field => selectedFields?.includes(field.value)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const defaultValue = [];
  const optionLabelName = 'label';
  const optionValueName = 'value';

  useEffect(() => {
    const triggerFieldSelectModal = () => setShowModal(true);
    window.parent.document.addEventListener('openFieldSelectModal', triggerFieldSelectModal);
    return () => {
      window.parent.document.removeEventListener('openFieldSelectModal', triggerFieldSelectModal);
    };
  }, []);

  const inventoryReportParams = { inventoryReport: { fields: selection.map(field => field.value) } };
  const compReportParams = { comp_report: { comp_attributes: selection.map(field => field.value) } };

  const saveFields = () => {
    setSaving(true);
    const params = inventoryReport ? inventoryReportParams : compReportParams;
    put(documentPath, params).then((data) => {
      if (data.success) {
        if (onSubmit) {
          onSubmit();
          setSaving(false);
          setShowModal(false);
        } else {
          window.location.reload();
        }
      } else {
        const errorMessage = data.error ? ` Error: ${data.error}` : '';
        setSaving(false);
        setError(`Failed to save fields. Please contact support.${errorMessage}`);
      }
    });
  };

  const saveButtonText = () => {
    if (saving) {
      return 'Saving...';
    } else {
      return 'Update Fields';
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setError(null);
  };

  return (
    <Modal
      className="multiselect-modal"
      container={window.parent.document.body}
      show={showModal}
      size="lg"
      onHide={() => setShowModal(false)}
    >
      <Modal.Header closeButton>
        <Modal.Title>Field Selection</Modal.Title>
      </Modal.Header>

      <Modal.Body className="flex-column">
        {error && (
          <Banner className="align-items-center mb-3" variant="destructive">
            <Banner.Content>
              <FontAwesomeIcon icon={faCircleXmark} size="lg" />
              {error}
            </Banner.Content>
          </Banner>
        )}
        <Cards className="flex-1 w-100">
          <SettingsContext.Provider value={{ optionLabelName, optionValueName }}>
            <AvailableOptions
              options={pdfFields}
              optionsLabel="Field"
              selection={selection}
              onAdd={added => setSelection([...selection, added])}
              onAddAll={() => setSelection(pdfFields)}
            />

            <SelectedOptions
              options={pdfFields}
              optionsLabel={'Columns'}
              reorderable
              selection={selection}
              onChange={setSelection}
              onRemoveAll={() => setSelection([...defaultValue])}
            />
          </SettingsContext.Provider>
        </Cards>
      </Modal.Body>

      <Modal.Footer>
        <Button.Cancel className="mr-2" onClick={closeModal}/>
        <Button.Primary disabled={!selection.length || saving || error} onClick={saveFields}>
          {saveButtonText()}
        </Button.Primary>
      </Modal.Footer>
    </Modal>
  );
}

DownloadableReportFieldSelector.propTypes = {
  documentPath: PropTypes.string.isRequired,
  inventoryReport: PropTypes.bool.isRequired,
  pdfFields: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  })).isRequired,
  selectedFields: PropTypes.arrayOf(PropTypes.string),
  onSubmit: PropTypes.func,
};
